import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Scissors, User, LogOut, Users, Shield, Settings, Sparkles, X, CreditCard, Layers } from 'lucide-react';

const MASTER_EMAIL = 'robson@tailorflow.tech';
const DEMO_EMAIL = 'demo@tailorflow.tech';
const isMasterAdmin = (user) => (user?.email || '').toLowerCase().trim() === MASTER_EMAIL;
const isDemoUser = (user) => (user?.email || '').toLowerCase().trim() === DEMO_EMAIL;

export default function Header() {
  const { user, logout } = useAuth();
  const { storeSettings } = useStore();
  const navigate = useNavigate();
  const isMaster = isMasterAdmin(user);
  const inDemo = isDemoUser(user);

  React.useEffect(() => {
    if (inDemo) {
      document.body.classList.add('demo-mode');
    } else {
      document.body.classList.remove('demo-mode');
    }
    return () => document.body.classList.remove('demo-mode');
  }, [inDemo]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const exitDemo = async () => {
    // Restore master admin session. Historically we stashed the master's
    // tokens in sessionStorage when entering demo mode and passed the
    // refresh token back through /auth/refresh — the endpoint accepts a
    // body-supplied refresh token AND re-sets its own httpOnly cookie in
    // response, so the browser leaves this call with the master's
    // identity cookie in place. Feb 2026 SEC-009 — no more localStorage
    // token writes; the httpOnly cookie is the sole source of truth.
    const mr = sessionStorage.getItem('master_refresh_token');
    sessionStorage.removeItem('master_access_token');
    sessionStorage.removeItem('master_refresh_token');
    sessionStorage.removeItem('in_demo_mode');
    localStorage.removeItem('selected_client_id');
    if (mr) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/auth/refresh`,
          { refresh_token: mr },
          { withCredentials: true },
        );
        window.location.href = '/dashboard';
        return;
      } catch (_e) {
        // Refresh failed — fall back to login page
      }
    }
    window.location.href = '/login';
  };

  return (
    <div className={`fixed top-0 inset-x-0 z-50 ${inDemo ? '' : ''}`}>
      {inDemo && (
        <div className="bg-[#C2A878] text-[#0A0A0C] px-3 sm:px-6 py-2 flex items-center justify-center flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold" data-testid="demo-banner">
          <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
          <span className="truncate">Demo — {storeSettings.store_name || 'Savile Row (Demo)'}</span>
          <button
            onClick={exitDemo}
            className="inline-flex items-center gap-1 border border-[#0A0A0C]/40 hover:bg-[#0A0A0C] hover:text-[#C2A878] px-2 sm:px-3 py-0.5 sm:py-1 transition-colors flex-shrink-0"
            data-testid="demo-exit-btn"
          >
            <X className="w-3 h-3" /> Exit
          </button>
        </div>
      )}
      <header className="glass border-b border-[#27272A] h-16 sm:h-20 flex items-center justify-between px-4 sm:px-6 md:px-8">
      <Link to="/dashboard" className="flex items-center gap-2 sm:gap-4 min-w-0" data-testid="header-logo">
        {storeSettings.store_logo ? (
          <img 
            src={storeSettings.store_logo} 
            alt={storeSettings.store_name} 
            className="h-8 sm:h-10 w-auto flex-shrink-0"
          />
        ) : (
          <div className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 border border-[#C2A878] flex-shrink-0">
            <Scissors className="w-4 h-4 sm:w-5 sm:h-5 text-[#C2A878]" />
          </div>
        )}
        <span 
          className="text-lg sm:text-xl font-light text-[#F5F5F5] tracking-tight truncate"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          {storeSettings.store_name || 'TailorFlow'}
        </span>
      </Link>

      <nav className="flex items-center gap-3 sm:gap-6">
        <Link 
          to="/dashboard" 
          className="text-sm text-[#A1A1AA] hover:text-[#C2A878] transition-colors hidden md:block"
          data-testid="nav-workspace"
        >
          Workspace
        </Link>
        <Link 
          to="/clients" 
          className="text-sm text-[#A1A1AA] hover:text-[#C2A878] transition-colors hidden md:block"
          data-testid="nav-clients"
        >
          Clients
        </Link>
        <Link 
          to="/fabrics" 
          className="text-sm text-[#A1A1AA] hover:text-[#C2A878] transition-colors hidden md:block"
          data-testid="nav-fabrics"
        >
          Fabrics
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#F5F5F5] hover:bg-transparent"
              data-testid="user-menu-trigger"
            >
              <div className="w-8 h-8 rounded-full bg-[#1A1A1E] border border-[#27272A] flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
              <span className="hidden sm:block text-sm">{user?.name}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="w-56 bg-[#121215] border border-[#27272A] text-[#F5F5F5]"
          >
            <div className="px-3 py-2 border-b border-[#27272A]">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-[#A1A1AA]">{user?.email}</p>
            </div>
            <DropdownMenuItem 
              className="cursor-pointer hover:bg-[#1A1A1E] focus:bg-[#1A1A1E] focus:text-[#F5F5F5]"
              onClick={() => navigate('/dashboard')}
              data-testid="menu-workspace"
            >
              <Scissors className="w-4 h-4 mr-2" />
              Workspace
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="cursor-pointer hover:bg-[#1A1A1E] focus:bg-[#1A1A1E] focus:text-[#F5F5F5]"
              onClick={() => navigate('/clients')}
              data-testid="menu-clients"
            >
              <Users className="w-4 h-4 mr-2" />
              Clients
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#27272A]" />
            <DropdownMenuItem 
              className="cursor-pointer hover:bg-[#1A1A1E] focus:bg-[#1A1A1E] focus:text-[#F5F5F5]"
              onClick={() => navigate('/billing')}
              data-testid="menu-billing"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Billing & Usage
            </DropdownMenuItem>
            {/* Feb 2026 — Account page (change password) available to
                every authenticated tailor, not just the master admin. */}
            <DropdownMenuItem 
              className="cursor-pointer hover:bg-[#1A1A1E] focus:bg-[#1A1A1E] focus:text-[#F5F5F5]"
              onClick={() => navigate('/account')}
              data-testid="menu-account"
            >
              <User className="w-4 h-4 mr-2" />
              Account
            </DropdownMenuItem>
            {/* Feb 2026 — Look Templates: every authenticated tailor can
                save any of their generated looks as a reusable template
                and prepopulate future clients with those exact specs. */}
            <DropdownMenuItem 
              className="cursor-pointer hover:bg-[#1A1A1E] focus:bg-[#1A1A1E] focus:text-[#F5F5F5]"
              onClick={() => navigate('/admin/templates')}
              data-testid="menu-look-templates"
            >
              <Layers className="w-4 h-4 mr-2" />
              Look Templates
            </DropdownMenuItem>
            {isMaster && (
              <DropdownMenuItem 
                className="cursor-pointer hover:bg-[#1A1A1E] focus:bg-[#1A1A1E] focus:text-[#F5F5F5]"
                onClick={() => navigate('/admin')}
                data-testid="menu-admin"
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin
              </DropdownMenuItem>
            )}
            {isMaster && (
              <DropdownMenuItem 
                className="cursor-pointer hover:bg-[#1A1A1E] focus:bg-[#1A1A1E] focus:text-[#F5F5F5]"
                onClick={() => navigate('/settings')}
                data-testid="menu-settings"
              >
                <Settings className="w-4 h-4 mr-2" />
                Store Settings
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              className="cursor-pointer text-red-400 hover:bg-[#1A1A1E] focus:bg-[#1A1A1E] focus:text-red-400"
              onClick={handleLogout}
              data-testid="menu-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    </header>
    </div>
  );
}
